<template>
    <!-- eslint-disable max-len -->
    <Disclosure v-slot="{ open }">
        <nav class="transition duration-50 fixed top-0 inset-x-0 z-20"
             :class="{'bg-slate-900/95 backdrop-blur-sm [@supports(backdrop-filter:blur(0))]:bg-slate-900/75': isScrolled || open, 'bg-transparent': !isScrolled && !open}">
            <div ref="el" class="mx-auto px-2 md:px-6 lg:px-8">
                <div class="relative flex h-16 items-center justify-between">
                    <div class="absolute inset-y-0 left-0 flex items-center md:hidden">
                        <DisclosureButton :class="['inline-flex items-center justify-center rounded-lg p-2', !isScrolled ? 'text-white' : 'text-slate-400 hover:text-white']">
                            <Bars3Icon v-if="!open" class="block h-6 w-6" />
                            <XMarkIcon v-else class="block h-6 w-6" />
                        </DisclosureButton>
                    </div>
                    <div class="flex flex-1 items-center justify-start pl-12 md:items-stretch">
                        <inertia-link class="flex flex-shrink-0 items-center" :href="route('home', $page.props.app.locale)">
                            <img :src="$page.props.assets['images/logo.png']"
                                 width="30"
                                 height="30"
                                 :alt="$page.props.app.title">
                        </inertia-link>
                        <div class="hidden md:ml-6 md:block">
                            <div class="flex justify-center space-x-6">
                                <inertia-link v-for="item in navigation"
                                              :key="item.name"
                                              :href="item.href"
                                              :class="['py-2 font-semibold', !isScrolled ? 'text-white': 'text-slate-400 hover:text-white']">
                                    {{ item.name }}
                                </inertia-link>
                            </div>
                        </div>
                    </div>
                    <div class="absolute inset-y-0 right-0 flex items-center pr-2 space-x-1 md:static md:inset-auto md:ml-6 md:pr-0">
                        <audio class="hidden" controls>
                            <source :src="track.path" type="audio/mpeg">
                        </audio>
                        <div v-if="isLoaded" class="flex items-center md:space-x-1 md:ml-auto">
                            <button :class="['rounded-full p-1', !isScrolled ? 'text-white' : 'text-slate-400 hover:text-white']"
                                    @click="prevTrack()">
                                <chevron-left-icon class="h6 w-6" />
                            </button>
                            <div class="hidden md:block md:text-sm md:text-white" href="#">
                                {{ timer(trackTime) }}
                            </div>
                            <button v-if="isPlaying"
                                    :class="['rounded-full p-1', !isScrolled ? 'text-white' : 'text-slate-400 hover:text-white']"
                                    @click="pauseTrack()">
                                <pause-icon class="h-6 w-6" />
                            </button>
                            <button v-else
                                    :class="['rounded-full p-1', !isScrolled ? 'text-white' : 'text-slate-400 hover:text-white']"
                                    @click="playTrack()">
                                <play-icon class="h-6 w-6" />
                            </button>
                            <button :class="['rounded-full p-1', !isScrolled ? 'text-white' : 'text-slate-400 hover:text-white']"
                                    @click="nextTrack()">
                                <chevron-right-icon class="h-6 w-6" />
                            </button>
                        </div>
                        <button type="button"
                                :class="['rounded-full p-1 mr-3', !isScrolled ? 'text-white' : 'text-slate-400 hover:text-white']"
                                @click.prevent="fullscreen()">
                            <ArrowsPointingOutIcon class="h-6 w-6" />
                        </button>
                        <HeadlessMenu as="div" class="relative">
                            <div>
                                <MenuButton :class="['flex items-center rounded-lg p-1 uppercase', !isScrolled ? 'text-white' : 'text-slate-400 hover:text-white']">
                                    {{ app.locale }}
                                    <ChevronDownIcon class="h-5 w-5" />
                                </MenuButton>
                            </div>
                            <transition enter-active-class="transition ease-out duration-100"
                                        enter-from-class="transform opacity-0 scale-95"
                                        enter-to-class="transform opacity-100 scale-100"
                                        leave-active-class="transition ease-in duration-75"
                                        leave-from-class="transform opacity-100 scale-100"
                                        leave-to-class="transform opacity-0 scale-95">
                                <MenuItems class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-lg bg-slate-800 py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
                                    <MenuItem v-for="(name, locale) in $page.props.transNames"
                                              v-slot="{ active }"
                                              :key="locale">
                                        <a :href="route('home', locale)"
                                           :class="[active ? 'bg-slate-700' : '', 'block px-4 py-2 text-sm text-slate-400']">
                                            {{ name }}
                                        </a>
                                    </MenuItem>
                                </MenuItems>
                            </transition>
                        </HeadlessMenu>
                        <HeadlessMenu as="div" class="relative">
                            <div>
                                <MenuButton :class="['flex items-center rounded-lg p-1 uppercase', !isScrolled ? 'text-white' : 'text-slate-400 hover:text-white']">
                                    <UserIcon class="h-5 w-5" />
                                    <ChevronDownIcon class="h-5 w-5" />
                                </MenuButton>
                            </div>
                            <transition enter-active-class="transition ease-out duration-100"
                                        enter-from-class="transform opacity-0 scale-95"
                                        enter-to-class="transform opacity-100 scale-100"
                                        leave-active-class="transition ease-in duration-75"
                                        leave-from-class="transform opacity-100 scale-100"
                                        leave-to-class="transform opacity-0 scale-95">
                                <MenuItems class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-lg bg-slate-800 py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
                                    <template v-if="$page.props.auth.user">
                                        <MenuItem v-slot="{ active }">
                                            <a :href="route('profile_edit', app.locale)" :class="[active ? 'bg-slate-700' : '', 'block px-4 py-2 text-sm text-slate-400']">
                                                {{ trans['messages.profile.edit'] }}
                                            </a>
                                        </MenuItem>
                                        <MenuItem v-slot="{ active }">
                                            <a :href="route('profile_change_password', app.locale)" :class="[active ? 'bg-slate-700' : '', 'block px-4 py-2 text-sm text-slate-400']">
                                                {{ trans['messages.user.change_password'] }}
                                            </a>
                                        </MenuItem>
                                        <MenuItem v-slot="{ active }">
                                            <form method="post" @submit.prevent="logout">
                                                <button :class="[active ? 'bg-slate-700' : '', 'block w-full text-left px-4 py-2 text-sm text-slate-400']">
                                                    {{ trans['messages.auth.logout'] }}
                                                </button>
                                            </form>
                                        </MenuItem>
                                    </template>
                                    <template v-else>
                                        <MenuItem v-slot="{ active }">
                                            <a :href="route('login', app.locale)" :class="[active ? 'bg-slate-700' : '', 'block px-4 py-2 text-sm text-slate-400']">
                                                {{ trans['messages.auth.login'] }}
                                            </a>
                                        </MenuItem>
                                        <MenuItem v-if="$page.props.app.register_enabled" v-slot="{ active }">
                                            <a :href="route('register', app.locale)" :class="[active ? 'bg-slate-700' : '', 'block px-4 py-2 text-sm text-slate-400']">
                                                {{ trans['messages.auth.register'] }}
                                            </a>
                                        </MenuItem>
                                    </template>
                                </MenuItems>
                            </transition>
                        </HeadlessMenu>
                    </div>
                </div>
            </div>
            <DisclosurePanel v-slot="{ close }" class="md:hidden">
                <div class="space-y-1 px-2 pt-2 pb-3">
                    <!-- eslint-disable vue/attribute-hyphenation -->
                    <inertia-link v-for="item in navigation"
                                  :key="item.name"
                                  :href="item.href"
                                  class="text-slate-400 hover:text-white hover:bg-black/25 block px-3 py-2 rounded-lg text-base"
                                  :onSuccess="close">
                        {{ item.name }}
                    </inertia-link>
                    <!-- eslint-enable vue/attribute-hyphenation -->
                </div>
            </DisclosurePanel>
        </nav>
    </Disclosure>
</template>

<script setup>
import {
    ArrowsPointingOutIcon,
    Bars3Icon,
    ChevronLeftIcon,
    ChevronRightIcon,
    PlayIcon,
    PauseIcon,
    XMarkIcon,
    UserIcon
} from '@heroicons/vue/24/outline';

import {
    ChevronDownIcon
} from '@heroicons/vue/20/solid';

import {
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    Menu as HeadlessMenu,
    MenuButton,
    MenuItem,
    MenuItems
} from '@headlessui/vue';

import {
    ref,
    onMounted,
    nextTick,
    watch,
    defineProps
} from 'vue';

import { router } from '@inertiajs/vue3';
import Screenfull from 'screenfull';

const { app, trans } = defineProps({
    app: {
        type: Object,
        required: true
    },

    routeName: {
        type: String,
        required: true
    },

    trans: {
        type: Object,
        required: true
    }
});

const isScrolled = ref(false);
const isLoaded = ref(false);
const isPlaying = ref(false);
const audioElement = ref(undefined);
const el = ref();
const track = ref({});
const trackInterval = ref(undefined);
const trackTime = ref(0);
const trackDuration = ref(0);
const tracks = ref([]);

const navigation = ref([
    { name: trans['messages.comic.plural'], href: route('comic', app.locale), current: false },
    { name: trans['messages.game'], href: route('fps', app.locale), current: false },
    { name: trans['messages.blog'], href: route('post', app.locale), current: false },
    { name: trans['messages.contact'], href: route('contact', app.locale), current: false }
]);

watch(trackTime, () => {
    document.dispatchEvent(new CustomEvent('track-timer', {
        detail: {
            trackTime: trackTime.value,
            trackDuration: trackDuration.value
        }
    }));
});

const updateTrack = () => {
    trackTime.value = audioElement.value.currentTime;
    trackDuration.value = audioElement.value.duration;
};

const loadTrack = newTrack => {
    isLoaded.value = false;
    isPlaying.value = false;
    track.value = newTrack;
    trackTime.value = 0;
    trackDuration.value = 0;
    audioElement.value.load();

    clearInterval(trackInterval.value);
    trackInterval.value = setInterval(updateTrack, 1000);
};

const playTrack = () => {
    if (!isPlaying.value) {
        audioElement.value.play();
    }
};

const loadAndPlayTrack = newTrack => {
    loadTrack(newTrack);
    playTrack();
};

const pauseTrack = () => {
    if (isPlaying.value) {
        audioElement.value.pause();
    }
};

const nextTrack = () => {
    const currentIndex = _.findIndex(
        tracks.value, currentTrack => currentTrack.id === track.value.id
    );

    if (currentIndex !== -1) {
        if (currentIndex < tracks.value.length - 1) {
            loadAndPlayTrack(tracks.value[currentIndex + 1]);
        } else if (currentIndex === tracks.value.length - 1) {
            loadAndPlayTrack(tracks.value[0]);
        }
    }
};

const prevTrack = () => {
    const currentIndex = _.findIndex(
        tracks.value, currentTrack => currentTrack.id === track.value.id
    );

    if (currentIndex > 0) {
        loadAndPlayTrack(tracks.value[currentIndex - 1]);
    } else if (currentIndex === 0) {
        loadAndPlayTrack(tracks.value[tracks.value.length - 1]);
    }
};

const initAudio = () => {
    audioElement.value = el.value.querySelector('audio');

    audioElement.value.addEventListener('canplay', () => {
        isLoaded.value = true;
    }, false);

    audioElement.value.addEventListener('play', () => {
        isPlaying.value = true;

        document.dispatchEvent(new CustomEvent('track', {
            detail: _.assignIn({}, track.value, {
                isPlaying: true
            })
        }));
    }, false);

    audioElement.value.addEventListener('pause', () => {
        isPlaying.value = false;

        document.dispatchEvent(new CustomEvent('track', {
            detail: _.assignIn({}, track.value, {
                isPlaying: false
            })
        }));
    }, false);

    audioElement.value.addEventListener('ended', nextTrack, false);
};

const addTrack = (newTrack, newTracks) => {
    track.value = newTrack;
    tracks.value = newTracks;

    nextTick(() => loadAndPlayTrack(track.value));
};

const seekTrackTo = seekTo => {
    if (trackDuration.value) {
        audioElement.value.currentTime = trackDuration.value * seekTo;
    }
};

const fullscreen = () => {
    if (Screenfull.isEnabled) {
        Screenfull.toggle();
    }
};

const logout = () => {
    router.post(route('logout'));
};

onMounted(() => {
    initAudio();
});

window.addEventListener('scroll', () => {
    isScrolled.value = window.scrollY > 0;
});

document.addEventListener('track-add', e => {
    addTrack(e.detail.track, e.detail.tracks);
}, false);

document.addEventListener('track-play', () => {
    playTrack();
}, false);

document.addEventListener('track-pause', () => {
    pauseTrack();
}, false);

document.addEventListener('track-seek-to', e => {
    seekTrackTo(e.detail.seekTo);
}, false);
</script>
